import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ArrowLeft, Calendar, Tag, Play } from 'lucide-react';
import Layout from '../components/layout/Layout';
import NewsCard from '../components/news/NewsCard';
import { newsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../components/seo/SEO';

const categoryLabels = {
  'ekonomi': 'Ekonomi',
  'politika': 'Politika',
  'vizyon-sektor': 'Vizyon Sektör',
  'dunya': 'Dünya',
  'teknoloji': 'Teknoloji',
  'finans': 'Finans',
};

const NewsDetailPage = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    fetchNewsDetail();
    setIsPlaying(false); // Reset video state when news changes
  }, [id]);

  const fetchNewsDetail = async () => {
    setLoading(true);
    try {
      const [newsRes, relatedRes] = await Promise.all([
        newsAPI.getById(id),
        newsAPI.getRelated(id),
      ]);
      setNews(newsRes.data);
      setRelatedNews(relatedRes.data || []);
    } catch (error) {
      console.error('Error fetching news detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtube\.com\/embed\/)([^?\s]+)/,
      /(?:youtu\.be\/)([^?\s]+)/,
      /(?:youtube\.com\/v\/)([^?\s]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Skeleton className="h-6 w-32 mb-6" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-8 w-3/4 mb-6" />
          <div className="flex gap-4 mb-8">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-24" />
          </div>
          <Skeleton className="aspect-video w-full rounded-lg mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!news) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Haber Bulunamadı</h1>
          <p className="text-gray-500 mb-6">Aradığınız haber mevcut değil veya kaldırılmış olabilir.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ana Sayfaya Dön
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const formattedDate = format(new Date(news.created_at), 'd MMMM yyyy, HH:mm', { locale: tr });
  const isVideo = news.youtube_url && news.category === 'vizyon-sektor';
  const videoId = isVideo ? getYouTubeId(news.youtube_url) : null;
  const thumbnailUrl = videoId 
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : news.image_url;

  return (
    <Layout>
      <SEO
        title={news.title}
        description={news.body ? news.body.substring(0, 180).replace(/\s+/g, ' ').trim() : undefined}
        image={news.image_url}
        type="article"
        publishedTime={news.created_at}
        category={categoryLabels[news.category] || news.category}
      />
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="news-detail">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#0284C7] transition-colors">Ana Sayfa</Link>
          <span>/</span>
          <Link 
            to={news.category === 'vizyon-sektor' ? '/vizyon-sektor' : `/kategori/${news.category}`}
            className="hover:text-[#0284C7] transition-colors"
          >
            {categoryLabels[news.category] || news.category}
          </Link>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-4xl font-extrabold text-[#0F172A] leading-tight mb-6" data-testid="news-title">
          {news.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
          <Badge className="bg-[#0284C7] hover:bg-[#0369A1]">
            <Tag className="w-3 h-3 mr-1" />
            {categoryLabels[news.category] || news.category}
          </Badge>
          <span className="flex items-center gap-1 text-gray-500 text-sm">
            <Calendar className="w-4 h-4" />
            {formattedDate}
          </span>
        </div>

        {/* Media */}
        {isVideo && videoId ? (
          <div className="aspect-video rounded-lg overflow-hidden mb-8 bg-black relative" data-testid="news-video">
            {isPlaying ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={news.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div 
                className="absolute inset-0 cursor-pointer group"
                onClick={() => setIsPlaying(true)}
              >
                <img
                  src={thumbnailUrl}
                  alt={news.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = news.image_url; }}
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                  <div className="bg-red-600 rounded-full p-5 transition-transform group-hover:scale-110 shadow-2xl">
                    <Play className="w-12 h-12 text-white fill-white" />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-video rounded-lg overflow-hidden mb-8" data-testid="news-image">
            <img
              src={news.image_url}
              alt={news.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none" data-testid="news-body">
          {news.body.split('\n').map((paragraph, index) => (
            <p key={`para-${index}`} className="text-gray-700 leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Related News */}
        {relatedNews.length > 0 && (
          <section className="mt-16 pt-8 border-t border-gray-200" data-testid="related-news">
            <h2 className="text-xl font-bold text-[#0F172A] mb-6">İLGİLİ HABERLER</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedNews.map((item) => (
                <NewsCard key={`related-${item.id}`} news={item} variant="horizontal" showCategory={false} />
              ))}
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
};

export default NewsDetailPage;
